import { useLazyQuery } from '@apollo/client';
import { TableCell, TableRow } from '@ovComponents/2-component/table/table';
import { UserContext } from '@providers/userContextProvider';
import { formatMoneyValue } from '@utils/moneyFormatting';
import type { Theme } from 'hephaestus/core';
import { Box, Skeleton, Typography, useTheme } from 'hephaestus/web';
import { useContext, useEffect, useMemo } from 'react';
import {
  FETCH_TRADE_ROW_PIPELINE,
  FETCH_TRADE_ROW_POSITION,
  FETCH_TRADE_ROW_SETTLED,
} from '../queries';
import { BORDERED_BOX_SX, SECTION_HEADER_SX, SMALL_SX } from '../styles';
import { isBuyType } from '../utils';

const STATE_LABELS: Record<string, { label: string; color?: string }> = {
  INITIATED: { label: 'Staged' },
  READY: { label: 'Ready' },
  REQUESTED: { label: 'Sent' },
  RECONCILED: { label: 'Filled' },
  CANCELED: { label: 'Canceled' },
};

const ContextCard = ({
  title,
  loading,
  children,
  theme,
  accent,
}: {
  title: string;
  loading: boolean;
  children: React.ReactNode;
  theme: Theme;
  accent?: string;
}) => (
  <Box
    sx={{
      flex: 1,
      border: '1px solid',
      borderColor: theme.color.outlineVariant,
      borderLeft: '3px solid',
      borderLeftColor: accent || theme.color.primary,
      borderRadius: 1,
      bgcolor: theme.color.surface,
      p: 1.5,
      minHeight: 110,
    }}
  >
    <Typography
      variant='labelSmall'
      component='div'
      sx={{
        ...SECTION_HEADER_SX,
        mb: 1,
        color: accent || theme.color.primary,
      }}
    >
      {title}
    </Typography>
    {loading ? (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75,
        }}
      >
        <Skeleton width='80%' height={16} />
        <Skeleton width='55%' height={16} />
        <Skeleton width='70%' height={16} />
      </Box>
    ) : (
      children
    )}
  </Box>
);

const MiniRow = ({
  cells,
  theme,
  header,
}: {
  cells: Array<{
    value: string;
    align?: 'right';
    bold?: boolean;
    color?: string;
  }>;
  theme: Theme;
  header?: boolean;
}) => (
  <Box
    sx={{
      display: 'flex',
      gap: 1,
      py: header ? 0.5 : 0.375,
      px: 0.75,
      borderBottom: '1px solid',
      borderColor: header ? theme.color.outline : theme.color.outlineVariant,

      ...(header
        ? { bgcolor: theme.color.neutralHover }
        : { '&:hover': { bgcolor: theme.color.neutralHover } }),
    }}
  >
    {cells.map((cell, i) => (
      <Typography
        key={i}
        variant='labelSmall'
        sx={{
          fontSize: header ? '0.6rem' : '0.7rem',
          flex: i === 0 ? '0 0 55px' : 1,
          textAlign: cell.align || 'left',
          fontWeight: cell.bold ? 700 : 400,
          letterSpacing: header ? '0.03em' : 0,
          ...(cell.color
            ? { color: cell.color }
            : {
                color: header
                  ? theme.color.onSurfaceVariant
                  : theme.color.onSurface,
              }),
        }}
      >
        {cell.value}
      </Typography>
    ))}
  </Box>
);

const MetricBlock = ({
  label,
  value,
  sub,
  theme,
  color,
}: {
  label: string;
  value: string;
  sub?: string;
  theme: Theme;
  color?: string;
}) => (
  <Box
    sx={{
      mb: 0.75,
    }}
  >
    <Typography
      variant='labelSmall'
      component='div'
      sx={{
        ...SMALL_SX,
        color: theme.color.onSurfaceVariant,
        letterSpacing: '0.03em',
        lineHeight: 1,
      }}
    >
      {label}
    </Typography>
    <Typography
      variant='bodySmall'
      component='div'
      sx={{
        fontWeight: 600,
        fontSize: '0.9rem',
        lineHeight: 1.3,
        color: color || theme.color.onSurface,
      }}
    >
      {value}
    </Typography>
    {sub && (
      <Typography
        variant='labelSmall'
        component='div'
        sx={{ ...SMALL_SX, color: theme.color.onSurfaceVariant }}
      >
        {sub}
      </Typography>
    )}
  </Box>
);

const EmptyHint = ({ text, theme }: { text: string; theme: Theme }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 60,
      opacity: 0.6,
    }}
  >
    <Typography
      variant='bodySmall'
      sx={{
        color: theme.color.onSurfaceVariant,
        fontStyle: 'italic',
        textAlign: 'center',
      }}
    >
      {text}
    </Typography>
  </Box>
);

type TradeRowDetailProps = {
  trade: any;
  colSpan: number;
  stateInfo: { label: string; description: string };
  isBuy: boolean;
  isBlocked: boolean;
};

const TradeRowDetail = ({
  trade,
  colSpan,
  stateInfo,
  isBuy,
  isBlocked,
}: TradeRowDetailProps) => {
  const theme = useTheme();
  const { activeOrganization } = useContext(UserContext);
  const requireVerification =
    !!activeOrganization?.requireSubTradeRequestApproval;

  const subAccountId = trade.subAccount?.id;
  const goalId = trade.subAccount?.goal?.id;
  const financialProductId = trade.financialProduct?.id;
  const organizationId = activeOrganization?.id;

  const [fetchPosition, { data: posData, loading: posLoading }] = useLazyQuery(
    FETCH_TRADE_ROW_POSITION,
    { fetchPolicy: 'cache-first' },
  );
  const [fetchPipeline, { data: pipeData, loading: pipeLoading }] =
    useLazyQuery(FETCH_TRADE_ROW_PIPELINE, { fetchPolicy: 'cache-first' });
  const [fetchSettled, { data: settledData, loading: settledLoading }] =
    useLazyQuery(FETCH_TRADE_ROW_SETTLED, { fetchPolicy: 'cache-first' });

  useEffect(() => {
    if (!subAccountId || !organizationId) return;
    const positionFilter = goalId
      ? { goalId, organizationId }
      : { subAccountId, organizationId };
    fetchPosition({
      variables: {
        input: {
          filter: positionFilter,
          pagination: { perPage: 1, offSet: 0 },
        },
      },
    });
    if (financialProductId) {
      fetchPipeline({
        variables: {
          input: {
            filter: { subAccountId, financialProductId },
            pagination: {
              sortField: 'createdAt',
              sortDesc: true,
              perPage: 6,
              offSet: 0,
            },
          },
        },
      });
      fetchSettled({
        variables: {
          input: {
            filter: {
              subAccountIds: [subAccountId],
              financialProductIds: [financialProductId],
              types: ['BUY', 'SELL'],
            },
            pagination: {
              sortField: 'date',
              sortDesc: true,
              perPage: 5,
              offSet: 0,
            },
          },
        },
      });
    }
  }, [
    subAccountId,
    goalId,
    organizationId,
    financialProductId,
    fetchPosition,
    fetchPipeline,
    fetchSettled,
  ]);

  const holding = useMemo(() => {
    const reports = posData?.fetchPortfolioReports?.portfolioReports;
    if (!reports?.length || !financialProductId) return null;
    const report = reports[0];
    const h = report.holdings?.find(
      (h: any) => h.financialProduct?.id === financialProductId,
    );
    if (!h) return null;

    let cashAvailableCents = report.cashAvailableForTradeCents ?? 0;
    let subAccountMarketValueCents = report.marketValueCents ?? 0;

    if (goalId && subAccountId && report.subAccounts?.length) {
      const sa = report.subAccounts.find(
        (s: any) => s.subAccount?.id === subAccountId,
      );
      if (sa) {
        cashAvailableCents =
          sa.cashAvailableForTradeCents ?? cashAvailableCents;
        subAccountMarketValueCents =
          sa.marketValueCents ?? subAccountMarketValueCents;
      }
    }

    return {
      quantity: h.quantity ?? 0,
      valueCents: h.valueCents ?? 0,
      pctOfPortfolio: h.expectedPercentage ?? 0,
      portfolioMarketValueCents: subAccountMarketValueCents,
      cashAvailableCents,
    };
  }, [posData, financialProductId, goalId, subAccountId]);

  const pipelineTrades = useMemo(() => {
    const strs = pipeData?.fetchSubTradeRequests?.subTradeRequests || [];
    return strs.filter((s: any) => s.id !== trade.id).slice(0, 5);
  }, [pipeData, trade.id]);

  const settledTxns = useMemo(
    () => settledData?.fetchTransactions?.transactions || [],
    [settledData],
  );

  return (
    <TableRow>
      <TableCell colSpan={colSpan}>
        <Box
          sx={{
            p: 2,
            bgcolor: theme.color.surface,
            ...BORDERED_BOX_SX,
            borderRadius: 1.5,
            boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
          }}
        >
          {/* Compact status + flags strip */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1.5,
              mb: 1.5,
              pb: 1.5,
              borderBottom: '1px solid',
              borderColor: theme.color.outlineVariant,
            }}
          >
            <Typography
              variant='bodySmall'
              sx={{ fontStyle: 'italic', color: theme.color.onSurfaceVariant }}
            >
              {stateInfo.description}
            </Typography>
            {requireVerification &&
              (trade.verifiedAt ? (
                <Typography
                  variant='labelSmall'
                  sx={{ ...SMALL_SX, color: theme.color.positive }}
                >
                  Verified{' '}
                  {trade.verifiedByOrganizationUser
                    ? `by ${trade.verifiedByOrganizationUser.firstName} ${trade.verifiedByOrganizationUser.lastName}`
                    : ''}{' '}
                  — {new Date(trade.verifiedAt).toLocaleString()}
                </Typography>
              ) : (
                <Typography
                  variant='labelSmall'
                  sx={{ ...SMALL_SX, color: theme.color.warning }}
                >
                  Not verified
                </Typography>
              ))}
            {trade.isSellAll && (
              <Typography
                variant='labelSmall'
                sx={{
                  ...SMALL_SX,
                  color: theme.color.warning,
                  fontWeight: 700,
                }}
              >
                SELL ALL
              </Typography>
            )}
            {trade.bulkTradeRun && (
              <Typography
                variant='labelSmall'
                sx={{ ...SMALL_SX, color: theme.color.primary }}
              >
                In Batch
              </Typography>
            )}
            {trade.pair && (
              <Typography variant='labelSmall' sx={{ ...SMALL_SX }}>
                Paired with {trade.pair.financialProduct?.ticker || '?'}
              </Typography>
            )}
            {trade.blockedUntilAccountActive && (
              <Typography
                variant='labelSmall'
                sx={{
                  ...SMALL_SX,
                  color: theme.color.negative,
                  fontWeight: 700,
                }}
              >
                Blocked — Account Inactive
              </Typography>
            )}
            {trade.blockedUntilSubAccountActive && (
              <Typography
                variant='labelSmall'
                sx={{
                  ...SMALL_SX,
                  color: theme.color.negative,
                  fontWeight: 700,
                }}
              >
                Blocked — Sub-Account Inactive
              </Typography>
            )}
            {trade.blockedUntil && (
              <Typography
                variant='labelSmall'
                sx={{ ...SMALL_SX, color: theme.color.warning }}
              >
                Blocked until {new Date(trade.blockedUntil).toLocaleString()}
              </Typography>
            )}
            {trade.schedule && (
              <Typography
                variant='labelSmall'
                sx={{ ...SMALL_SX, color: theme.color.warning }}
              >
                Schedule: {trade.schedule.name}
              </Typography>
            )}
            {isBuy &&
              trade.state === 'INITIATED' &&
              !isBlocked &&
              !trade.bulkTradeRun && (
                <Typography
                  variant='labelSmall'
                  sx={{ ...SMALL_SX, color: theme.color.warning }}
                >
                  Likely insufficient cash — unable to fund this BUY
                </Typography>
              )}
          </Box>

          {/* Lazy-loaded context cards */}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
            }}
          >
            {/* Current Position */}
            <ContextCard
              title='CURRENT POSITION'
              loading={posLoading}
              theme={theme}
              accent={theme.color.primary}
            >
              {holding ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 2,
                      mb: 0.5,
                    }}
                  >
                    <MetricBlock
                      label='SHARES'
                      value={holding.quantity.toFixed(4)}
                      theme={theme}
                    />
                    <MetricBlock
                      label='MARKET VALUE'
                      value={formatMoneyValue(holding.valueCents, '$', 2)}
                      theme={theme}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 2,
                    }}
                  >
                    <MetricBlock
                      label='% OF PORTFOLIO'
                      value={`${(holding.pctOfPortfolio * 100).toFixed(1)}%`}
                      theme={theme}
                    />
                    <MetricBlock
                      label='CASH AVAILABLE'
                      value={formatMoneyValue(
                        holding.cashAvailableCents,
                        '$',
                        2,
                      )}
                      theme={theme}
                      color={theme.color.positive}
                    />
                  </Box>
                </Box>
              ) : (
                <EmptyHint text='No position data available' theme={theme} />
              )}
            </ContextCard>

            {/* Pipeline Activity */}
            <ContextCard
              title='PIPELINE ACTIVITY'
              loading={pipeLoading}
              theme={theme}
              accent={theme.color.warning}
            >
              {pipelineTrades.length > 0 ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <MiniRow
                    theme={theme}
                    header
                    cells={[
                      { value: 'DATE', bold: true },
                      { value: 'SIDE', bold: true },
                      { value: 'AMOUNT', align: 'right', bold: true },
                      { value: 'STATUS', bold: true },
                    ]}
                  />
                  {pipelineTrades.map((s: any) => {
                    const sl = STATE_LABELS[s.state] || { label: s.state };
                    const buy = isBuyType(s.type);
                    return (
                      <MiniRow
                        key={s.id}
                        theme={theme}
                        cells={[
                          {
                            value: s.createdAt
                              ? new Date(s.createdAt).toLocaleDateString(
                                  undefined,
                                  { month: 'numeric', day: 'numeric' },
                                )
                              : '—',
                          },
                          {
                            value: buy ? 'BUY' : 'SELL',
                            color: buy
                              ? theme.color.positive
                              : theme.color.negative,
                            bold: true,
                          },
                          {
                            value: formatMoneyValue(
                              Math.abs(s.moneyAllocatedCents || 0),
                              '$',
                              2,
                            ),
                            align: 'right',
                          },
                          { value: sl.label },
                        ]}
                      />
                    );
                  })}
                </Box>
              ) : (
                <EmptyHint
                  text='No other pipeline trades for this security'
                  theme={theme}
                />
              )}
            </ContextCard>

            {/* Settled History */}
            <ContextCard
              title='SETTLED HISTORY'
              loading={settledLoading}
              theme={theme}
              accent={theme.color.positive}
            >
              {settledTxns.length > 0 ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <MiniRow
                    theme={theme}
                    header
                    cells={[
                      { value: 'DATE', bold: true },
                      { value: 'SIDE', bold: true },
                      { value: 'QTY', align: 'right', bold: true },
                      { value: 'PRICE', align: 'right', bold: true },
                      { value: 'VALUE', align: 'right', bold: true },
                    ]}
                  />
                  {settledTxns.map((tx: any) => {
                    const txBuy = tx.type === 'BUY';
                    return (
                      <MiniRow
                        key={tx.id}
                        theme={theme}
                        cells={[
                          {
                            value: tx.date
                              ? new Date(tx.date).toLocaleDateString(
                                  undefined,
                                  { month: 'numeric', day: 'numeric' },
                                )
                              : '—',
                          },
                          {
                            value: txBuy ? 'BUY' : 'SELL',
                            color: txBuy
                              ? theme.color.positive
                              : theme.color.negative,
                            bold: true,
                          },
                          {
                            value: (tx.quantity ?? 0).toFixed(2),
                            align: 'right',
                          },
                          {
                            value: tx.priceCents
                              ? formatMoneyValue(tx.priceCents, '$', 2)
                              : '—',
                            align: 'right',
                          },
                          {
                            value: formatMoneyValue(
                              Math.abs(tx.valueCents || 0),
                              '$',
                              2,
                            ),
                            align: 'right',
                            bold: true,
                          },
                        ]}
                      />
                    );
                  })}
                </Box>
              ) : (
                <EmptyHint text='No settled trades found' theme={theme} />
              )}
            </ContextCard>
          </Box>
        </Box>
      </TableCell>
    </TableRow>
  );
};

export default TradeRowDetail;
